/**
 * Minimal i18n: English is the source copy AND the dictionary key. Default
 * language is English; Chinese is opt-in and persisted per device.
 *
 * `t("Copy")` → "Copy" (en) / "复制" (zh). Placeholders use `{name}` and are
 * substituted after lookup so one key serves both languages:
 * `t("Round {n}", { n: 3 })`.
 */
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { ZH } from "./zh";

export type Lang = "en" | "zh";

const STORAGE_KEY = "humanai.lang";

export type TFunc = (
  key: string,
  params?: Record<string, string | number>,
) => string;

interface I18nValue {
  lang: Lang;
  setLang: (l: Lang) => void;
  t: TFunc;
}

const I18nContext = createContext<I18nValue | null>(null);

function loadLang(): Lang {
  try {
    const v = window.localStorage.getItem(STORAGE_KEY);
    if (v === "zh" || v === "en") return v;
  } catch {
    // storage unavailable (private mode etc.) — fall through
  }
  return "en";
}

function interpolate(s: string, params?: Record<string, string | number>): string {
  if (!params) return s;
  return s.replace(/\{(\w+)\}/g, (m, name: string) =>
    name in params ? String(params[name]) : m,
  );
}

export function I18nProvider({ children }: { children: ReactNode }) {
  const [lang, setLangState] = useState<Lang>(loadLang);

  const setLang = useCallback((l: Lang) => {
    setLangState(l);
    try {
      window.localStorage.setItem(STORAGE_KEY, l);
    } catch {
      // best-effort persistence only
    }
    document.documentElement.lang = l === "zh" ? "zh-CN" : "en";
  }, []);

  const t = useCallback<TFunc>(
    (key, params) => interpolate(lang === "zh" ? ZH[key] ?? key : key, params),
    [lang],
  );

  const value = useMemo(() => ({ lang, setLang, t }), [lang, setLang, t]);
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18nValue {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error("useI18n must be used inside <I18nProvider>");
  return ctx;
}

/** EN / 中文 switch, shared by the landing nav and the app shell. */
export function LangToggle() {
  const { lang, setLang } = useI18n();
  return (
    <div className="lang-toggle" role="group" aria-label="Language">
      <button
        type="button"
        className={`lang-toggle__btn${lang === "en" ? " lang-toggle__btn--active" : ""}`}
        onClick={() => setLang("en")}
      >
        EN
      </button>
      <button
        type="button"
        className={`lang-toggle__btn${lang === "zh" ? " lang-toggle__btn--active" : ""}`}
        onClick={() => setLang("zh")}
      >
        中文
      </button>
    </div>
  );
}
